
'use client';
import { getMessaging, getToken, deleteToken } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { firebaseApp, firestore } from './client';

export const getFCMTokenAndSave = async (userId: string) => {
  // Bail early if the API is unavailable or the user has already declined.
  // Calling `requestPermission()` when state is `denied` (including the
  // browser's auto-block after repeated dismissals) triggers a console
  // intervention warning we cannot otherwise silence.
  if (typeof window === 'undefined' || !('Notification' in window)) return null;
  if (Notification.permission === 'denied') return null;

  try {
    const permission = Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const messaging = getMessaging(firebaseApp);
    const vapidKey = process.env.NEXT_PUBLIC_VAPID_KEY;

    if (!vapidKey || vapidKey === 'YOUR_PUBLIC_VAPID_KEY_FROM_FIREBASE_CONSOLE') {
      console.error("VAPID key not found. Please set NEXT_PUBLIC_VAPID_KEY in your .env file.");
      return null;
    }

    const fcmToken = await getToken(messaging, { vapidKey });
    if (fcmToken) {
      const tokenRef = doc(firestore, `users/${userId}/fcmTokens/${fcmToken}`);
      await setDoc(tokenRef, { createdAt: serverTimestamp() }, { merge: true });
      return fcmToken;
    }
  } catch (error) {
    console.error('An error occurred while retrieving FCM token.', error);
  }
  return null;
};

/**
 * Unregister this device's push token on logout. Without this, a shared
 * browser would continue to deliver pushes addressed to the previous user
 * because the FCM service-worker registration survives Firebase sign-out
 * and the token doc remains under `users/{oldUid}/fcmTokens/{token}`. Both
 * the Firestore record and the FCM-side registration are removed so the
 * next sign-in gets a fresh token.
 * @param {string} userId  UID of the user logging out.
 */
export const deleteFCMTokenOnLogout = async (userId: string) => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  // Bail out unless the user has explicitly granted notifications. If
  // permission is `default` (never asked or revoked back to default),
  // getToken() will trigger the browser permission prompt mid-logout —
  // a confusing UX where signing out asks the user to enable notifications.
  // If permission is `denied`, no token can possibly exist anyway.
  if (Notification.permission !== 'granted') return;
  try {
    const messaging = getMessaging(firebaseApp);
    const vapidKey = process.env.NEXT_PUBLIC_VAPID_KEY;
    if (!vapidKey) return;
    // getToken is the only way to retrieve the current device token without
    // storing it ourselves; returns '' if no token has been minted yet.
    const fcmToken = await getToken(messaging, { vapidKey }).catch(() => '');
    if (fcmToken) {
      await deleteDoc(doc(firestore, `users/${userId}/fcmTokens/${fcmToken}`))
        .catch((e) => console.warn('Failed to remove fcmToken doc on logout:', e));
    }
    // Revoke the token at the FCM layer so the same browser session issues
    // a fresh one for the next user to sign in.
    await deleteToken(messaging).catch(() => {/* best-effort */});
  } catch (err) {
    console.warn('deleteFCMTokenOnLogout failed:', err);
  }
};
